import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { switchMap, tap, catchError, finalize, map } from 'rxjs/operators';
import {
    ProgressState,
    ProgressOperationConfig,
    ProgressStep,
    StepOperation,
    StepProgressTracker
} from 'src/app/_shared/components/progress/models/progress-definitions';
import { getProgressConfig } from 'src/app/_shared/components/progress/config/progress-operations.config';

@Injectable({ providedIn: 'root' })
export class ProgressTrackerService implements OnDestroy {

    // =========================================================================
    // STATE
    // =========================================================================

    private readonly initialState: ProgressState = {
        currentStepIndex: 0,
        currentStepProgress: 0,
        overallProgress: 0,
        status: 'idle'
    };

    private state$ = new BehaviorSubject<ProgressState>(this.initialState);
    private config$ = new BehaviorSubject<ProgressOperationConfig | null>(null);
    private destroy$ = new Subject<void>();

    private animationFrameId: number | null = null;
    private stepStartTime: number = 0;
    private _currentConfig: ProgressOperationConfig | null = null;

    // =========================================================================
    // PUBLIC OBSERVABLES
    // =========================================================================

    /** État courant de la progression */
    readonly progress$ = this.state$.asObservable();

    /** Configuration courante (Observable) */
    readonly currentConfig$ = this.config$.asObservable();

    /** Configuration courante (synchrone, pour vérification dans les directives) */
    get currentConfig(): ProgressOperationConfig | null {
        return this._currentConfig;
    }

    /** Indique si une opération est en cours */
    readonly isRunning$ = this.state$.pipe(
        map(state => state.status === 'running')
    );

    // =========================================================================
    // API PRINCIPALE
    // =========================================================================

    /**
     * Exécute une opération avec tracking de progression
     *
     * @param operationType Type d'opération (ex: 'GDL1', 'BALCPT')
     * @param stepOperations Liste des opérations pour chaque étape
     * @returns Observable du résultat final
     *
     * @example
     * this.progressTracker.execute('GDL1', [
     *     () => this.fetchData$,
     *     (_, data) => this.generatePdf$(data),
     *     (tracker, pdfUrl) => this.extractImages$(pdfUrl, tracker)
     * ]).subscribe(images => {
     *     this.images = images;
     * });
     */
    execute<T>(
        operationType: string,
        stepOperations: StepOperation[]
    ): Observable<T> {

        const config = getProgressConfig(operationType);

        // Validation
        if (stepOperations.length !== config.steps.length) {
            console.warn(
                `[ProgressTracker] Mismatch: ${stepOperations.length} operations for ${config.steps.length} steps`
            );
        }

        // Initialisation
        this._currentConfig = config;
        this.config$.next(config);
        this.start();

        // Exécution séquentielle des étapes
        return this.executeStepsSequentially<T>(config, stepOperations).pipe(
            tap(() => this.complete()),
            catchError(error => {
                this.setError(error?.message || 'Une erreur est survenue');
                throw error;
            }),
            finalize(() => {
                // Reset après un délai pour laisser voir le 100%
                setTimeout(() => this.reset(), 800);
            })
        );
    }

    /**
     * Annule l'opération en cours
     */
    cancel(): void {
        this.stopAnimation();
        this.updateState({ status: 'idle' });
        this.reset();
    }

    // =========================================================================
    // EXÉCUTION DES ÉTAPES
    // =========================================================================

    private executeStepsSequentially<T>(
        config: ProgressOperationConfig,
        operations: StepOperation[]
    ): Observable<T> {

        let chain$: Observable<any> = of(null);

        operations.forEach((operation, index) => {
            chain$ = chain$.pipe(
                switchMap(previousResult => {
                    return this.executeStep(config, index, operation, previousResult);
                })
            );
        });

        return chain$ as Observable<T>;
    }

    private executeStep<T>(
        config: ProgressOperationConfig,
        stepIndex: number,
        operation: StepOperation<T>,
        previousResult: any
    ): Observable<T> {

        const step = config.steps[stepIndex];

        // Démarrer l'étape
        this.startStep(stepIndex, config);

        // Créer le tracker si l'étape supporte le progress réel
        const tracker: StepProgressTracker | null = step.hasRealProgress
            ? this.createStepTracker()
            : null;

        // Résoudre l'opération (fonction ou observable direct)
        const operation$ = typeof operation === 'function'
            ? operation(tracker, previousResult)
            : operation;

        return operation$.pipe(
            tap(() => this.completeStep(stepIndex, config))
        );
    }

    private createStepTracker(): StepProgressTracker {
        return {
            setProgress: (percent: number, details?: string) => {
                this.setRealProgress(percent, details);
            }
        };
    }

    // =========================================================================
    // GESTION DES ÉTAPES
    // =========================================================================

    private start(): void {
        this.updateState({
            currentStepIndex: 0,
            currentStepProgress: 0,
            overallProgress: 0,
            status: 'running',
            details: undefined,
            errorMessage: undefined
        });
    }

    private startStep(stepIndex: number, config: ProgressOperationConfig): void {
        this.stopAnimation();

        const overallProgress = this.calculateOverallProgress(config, stepIndex, 0);

        this.updateState({
            currentStepIndex: stepIndex,
            currentStepProgress: 0,
            overallProgress,
            details: undefined
        });

        // Démarrer l'animation fake
        const step = config.steps[stepIndex];
        if (!step.hasRealProgress) {
            this.stepStartTime = Date.now();
            this.startFakeProgressAnimation(step, config);
        }
    }

    private completeStep(stepIndex: number, config: ProgressOperationConfig): void {
        this.stopAnimation();

        const isLastStep = stepIndex === config.steps.length - 1;
        const overallProgress = this.calculateOverallProgress(config, stepIndex, 100);

        this.updateState({
            currentStepProgress: 100,
            overallProgress: isLastStep ? 100 : overallProgress
        });
    }

    private complete(): void {
        this.stopAnimation();
        this.updateState({
            currentStepProgress: 100,
            overallProgress: 100,
            status: 'completed'
        });
    }

    private setError(message: string): void {
        this.stopAnimation();
        this.updateState({
            status: 'error',
            errorMessage: message
        });
    }

    private reset(): void {
        this.stopAnimation();
        this._currentConfig = null;
        this.config$.next(null);
        this.state$.next(this.initialState);
    }

    // =========================================================================
    // ANIMATION FAKE PROGRESS
    // =========================================================================

    /**
     * Animation fluide en 3 phases :
     * - Phase 1 (0-70%): Progression rapide pour donner l'impression que ça avance
     * - Phase 2 (70-85%): Progression modérée
     * - Phase 3 (85-95%): Progression très lente ("crawling") qui ne s'arrête jamais
     *
     * Cette approche évite l'effet "bloqué" que ressent l'utilisateur
     */
    private startFakeProgressAnimation(step: ProgressStep, config: ProgressOperationConfig): void {
        const estimatedDuration = step.estimatedDurationMs ?? 10000;

        // Paramètres des phases
        const PHASE1_TARGET = 70;   // Atteint rapidement
        const PHASE2_TARGET = 85;   // Atteint en temps estimé
        const MAX_TARGET = 95;      // Ne jamais dépasser

        // Durées relatives des phases
        const PHASE1_DURATION_RATIO = 0.3;  // 30% du temps pour atteindre 70%
        const PHASE2_DURATION_RATIO = 0.7;  // 70% du temps pour atteindre 85%

        const phase1Duration = estimatedDuration * PHASE1_DURATION_RATIO;
        const phase2Duration = estimatedDuration * PHASE2_DURATION_RATIO;

        const animate = () => {
            const elapsed = Date.now() - this.stepStartTime;
            let fakeProgress: number;

            if (elapsed < phase1Duration) {
                // Phase 1: Progression rapide (0 → 70%)
                const ratio = elapsed / phase1Duration;
                fakeProgress = this.easeOutQuad(ratio) * PHASE1_TARGET;

            } else if (elapsed < estimatedDuration) {
                // Phase 2: Progression modérée (70% → 85%)
                const phase2Elapsed = elapsed - phase1Duration;
                const ratio = phase2Elapsed / phase2Duration;
                fakeProgress = PHASE1_TARGET + this.easeOutQuad(ratio) * (PHASE2_TARGET - PHASE1_TARGET);

            } else {
                // Phase 3: Crawling asymptotique (85% → 95%, ne s'arrête jamais)
                const overtime = elapsed - estimatedDuration;
                const crawlProgress = (MAX_TARGET - PHASE2_TARGET) * (1 - Math.exp(-overtime / 10000));
                fakeProgress = PHASE2_TARGET + crawlProgress;
            }

            this.updateStepProgress(Math.min(fakeProgress, MAX_TARGET), config, this.getWaitingMessage(step, elapsed));

            // Toujours continuer l'animation tant qu'on est en running
            const state = this.state$.value;
            if (state.status === 'running') {
                this.animationFrameId = requestAnimationFrame(animate);
            }
        };

        this.animationFrameId = requestAnimationFrame(animate);
    }

    /**
     * Dernier message d'attente dont le délai est atteint (aucun si l'étape n'en déclare pas)
     */
    private getWaitingMessage(step: ProgressStep, elapsed: number): string | undefined {
        return step.waitingMessages?.filter(waiting => waiting.afterMs <= elapsed).pop()?.message;
    }

    /**
     * Courbe ease-out quadratique (plus douce que cubic)
     */
    private easeOutQuad(t: number): number {
        const clamped = Math.min(t, 1);
        return 1 - (1 - clamped) * (1 - clamped);
    }

    private stopAnimation(): void {
        if (this.animationFrameId !== null) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
        }
    }

    // =========================================================================
    // PROGRESS RÉEL
    // =========================================================================

    /**
     * Appelé par le StepTracker pour reporter un progress réel
     */
    private setRealProgress(percent: number, details?: string): void {
        this.stopAnimation();

        if (this._currentConfig) {
            this.updateStepProgress(percent, this._currentConfig, details);
        }
    }

    // =========================================================================
    // CALCULS
    // =========================================================================

    private updateStepProgress(
        stepProgress: number,
        config: ProgressOperationConfig,
        details?: string
    ): void {
        const state = this.state$.value;
        const overallProgress = this.calculateOverallProgress(
            config,
            state.currentStepIndex,
            stepProgress
        );

        this.updateState({
            currentStepProgress: stepProgress,
            overallProgress,
            details
        });
    }

    /**
     * Calcule le progress global basé sur les poids des étapes
     */
    private calculateOverallProgress(
        config: ProgressOperationConfig,
        stepIndex: number,
        stepProgress: number
    ): number {
        const totalWeight = config.steps.reduce((sum, s) => sum + s.weight, 0);

        // Poids des étapes complétées
        let completedWeight = 0;
        for (let i = 0; i < stepIndex; i++) {
            completedWeight += config.steps[i].weight;
        }

        // Contribution de l'étape courante
        const currentStepWeight = config.steps[stepIndex]?.weight ?? 0;
        const currentContribution = (currentStepWeight * stepProgress) / 100;

        return ((completedWeight + currentContribution) / totalWeight) * 100;
    }

    // =========================================================================
    // HELPERS
    // =========================================================================

    private updateState(partial: Partial<ProgressState>): void {
        this.state$.next({
            ...this.state$.value,
            ...partial
        });
    }

    // =========================================================================
    // LIFECYCLE
    // =========================================================================

    ngOnDestroy(): void {
        this.stopAnimation();
        this.destroy$.next();
        this.destroy$.complete();
    }
}
